import { useEffect, useState, useSyncExternalStore } from 'react';

/**
 * The soft keyboard, as the overlays see it.
 *
 * Two sources report it, and they disagree by platform:
 *
 *   - Mobile Safari shrinks `window.visualViewport` while the keyboard is up.
 *     Floating UI already clips menus to the visual viewport there, so a menu
 *     flips above the field on its own.
 *   - The native CRM app (Capacitor WKWebView, Keyboard plugin in `resize:
 *     'body'` mode) does NOT shrink the visual viewport or the layout
 *     viewport. Fixed overlays and Floating UI both believe the whole screen
 *     is visible, so a centered dialog and the menu under its field sit
 *     behind the keyboard (PD-15697, PD-14097). The native shell's
 *     `NativeShellSetup` is the only source there: on every keyboard show and
 *     hide it writes the height to the `--keyboard-height` custom property on
 *     `<html>`, toggles the `keyboard-open` class, and dispatches
 *     `app-keyboard-height` on `window`.
 */

/** Dispatched on `window` by the native shell after it publishes a height. */
export const KEYBOARD_HEIGHT_EVENT = 'app-keyboard-height';

/**
 * The keyboard height (px) the native shell last published, or 0 when it is
 * closed, nothing has been published (desktop, mobile Safari, the storefront)
 * or there is no DOM (node-environment tests).
 */
export function readPublishedKeyboardHeight(): number {
	if (typeof document === 'undefined') {
		return 0;
	}
	const raw = getComputedStyle(document.documentElement).getPropertyValue(
		'--keyboard-height',
	);
	const parsed = Number.parseFloat(raw);
	return Number.isFinite(parsed) && parsed > 1 ? Math.round(parsed) : 0;
}

/**
 * Height (px) of the layout viewport currently occluded by the on-screen
 * keyboard. On iOS (Safari and Capacitor WKWebView) the soft keyboard does
 * NOT shrink the layout viewport, so a `fixed; bottom: 0` panel keeps
 * extending underneath it and its internal scroll area can never reach the
 * bottom fields. Lifting the panel's `bottom` by this inset keeps the whole
 * form reachable while the keyboard is up. Returns 0 when the keyboard is
 * closed, when the platform resizes the layout viewport itself (Android
 * browsers), or when neither source reports anything.
 *
 * PD-15697 (reopened) — the visual-viewport math is correct in mobile Safari,
 * but inside the Capacitor WKWebView `visualViewport` does not shrink, so the
 * hook reads BOTH sources and uses whichever reports more occlusion. Taking
 * the larger (rather than the sum) is what keeps the Sidebar lifting exactly
 * once on a platform that reports through both.
 *
 * Used by the editor Sidebar. Overlays that should change only inside the
 * native app read the native height alone instead (Modal through
 * `usePublishedKeyboardHeight`, the select menus through
 * `useKeyboardAwareFloating`), because mobile Safari already handles them.
 */
export function useKeyboardBottomInset(enabled: boolean): number {
	const [inset, setInset] = useState(0);

	useEffect(() => {
		if (!enabled) {
			setInset(0);
			return undefined;
		}
		const vv = window.visualViewport;

		const update = (): void => {
			const occluded =
				vv != null ? window.innerHeight - vv.height - vv.offsetTop : 0;
			// Ignore sub-pixel noise from browser chrome show/hide; take the
			// larger of the browser-reported and native-published values.
			setInset(
				Math.max(
					occluded > 1 ? Math.round(occluded) : 0,
					readPublishedKeyboardHeight(),
				),
			);
		};

		update();
		vv?.addEventListener('resize', update);
		vv?.addEventListener('scroll', update);
		// Fired by the native shell whenever the Keyboard plugin reports a
		// height change — the only reliable signal inside the WKWebView.
		window.addEventListener(KEYBOARD_HEIGHT_EVENT, update);

		return (): void => {
			vv?.removeEventListener('resize', update);
			vv?.removeEventListener('scroll', update);
			window.removeEventListener(KEYBOARD_HEIGHT_EVENT, update);
		};
	}, [enabled]);

	return inset;
}

// One module-level store for the natively published height. Every open
// select menu and every open Modal reads it, and a form can hold dozens of
// selects, so they share a single window listener instead of adding one each.
let cachedPublishedHeight = 0;
const publishedHeightListeners = new Set<() => void>();

function handlePublishedHeightEvent(): void {
	const next = readPublishedKeyboardHeight();
	if (next === cachedPublishedHeight) {
		return;
	}
	cachedPublishedHeight = next;
	for (const listener of publishedHeightListeners) {
		listener();
	}
}

/**
 * Calls `listener` whenever the natively published keyboard height changes.
 * The first subscriber attaches the single window listener and the last one
 * to leave removes it. Returns the unsubscribe function.
 */
export function subscribeToPublishedKeyboardHeight(
	listener: () => void,
): () => void {
	if (publishedHeightListeners.size === 0) {
		// Nothing kept the cached value current while nobody was listening.
		cachedPublishedHeight = readPublishedKeyboardHeight();
		window.addEventListener(KEYBOARD_HEIGHT_EVENT, handlePublishedHeightEvent);
	}
	publishedHeightListeners.add(listener);

	return (): void => {
		publishedHeightListeners.delete(listener);
		if (publishedHeightListeners.size === 0) {
			window.removeEventListener(
				KEYBOARD_HEIGHT_EVENT,
				handlePublishedHeightEvent,
			);
		}
	};
}

/**
 * The current natively published keyboard height (px). Cheap while anything
 * is subscribed (it returns the value the window listener keeps); with no
 * subscriber the cached value may be stale, so it reads the live one.
 */
export function getPublishedKeyboardHeight(): number {
	return publishedHeightListeners.size === 0
		? readPublishedKeyboardHeight()
		: cachedPublishedHeight;
}

function getPublishedKeyboardHeightOnServer(): number {
	return 0;
}

/**
 * The keyboard height (px) published by the native shell, kept current as
 * the keyboard opens, closes and changes height. 0 everywhere the native
 * shell is absent, so desktop and mobile Safari are unaffected: Safari's
 * keyboard is already visible to Floating UI and to the layout through the
 * visual viewport, and adding this on top would count the keyboard twice.
 */
export function usePublishedKeyboardHeight(): number {
	return useSyncExternalStore(
		subscribeToPublishedKeyboardHeight,
		getPublishedKeyboardHeight,
		getPublishedKeyboardHeightOnServer,
	);
}
